// ============================================================
// AuditManager.jsx
// Modal panel for: Save current audit | Open saved audit | Start new audit
// Shown as an overlay triggered from any step
// ============================================================

import React, { useState, useEffect } from "react";
import { listAudits, deleteAudit } from "../utils/auditStorage";

export default function AuditManager({
  currentAuditId,
  auditInfo,
  hasUnsavedChanges,
  onSave,
  onLoad,
  onNew,
  onClose,
}) {
  const [savedAudits, setSavedAudits] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmNew, setConfirmNew] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setSavedAudits(listAudits());
  }, []);

  const handleSave = () => {
    onSave();
    setSaveSuccess(true);
    setSavedAudits(listAudits());
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleDelete = (id) => {
    deleteAudit(id);
    setSavedAudits(listAudits());
    setConfirmDelete(null);
  };

  const handleLoad = (audit) => {
    onLoad(audit);
    onClose();
  };

  const handleNew = () => {
    if (hasUnsavedChanges) {
      setConfirmNew(true);
    } else {
      onNew();
      onClose();
    }
  };

  const confirmNewAudit = () => {
    setConfirmNew(false);
    onNew();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">Audit Manager</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Current audit actions */}
        <div className="modal-section">
          <div className="modal-section-title">Current Audit</div>
          <div className="current-audit-info">
            <div className="current-audit-name">
              {auditInfo.companyName || "Unsaved Audit"}
              {auditInfo.facilityName ? ` — ${auditInfo.facilityName}` : ""}
            </div>
            {auditInfo.auditDate && (
              <div className="current-audit-meta">Audit Date: {auditInfo.auditDate}</div>
            )}
            {currentAuditId && (
              <div className="current-audit-meta saved-badge">✓ Previously saved</div>
            )}
          </div>

          <div className="modal-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              {currentAuditId ? "💾 Save / Update" : "💾 Save Audit"}
            </button>
            <button className="btn btn-outline" onClick={handleNew}>
              ＋ Start New Audit
            </button>
          </div>

          {saveSuccess && (
            <div className="save-success">✓ Audit saved successfully.</div>
          )}
        </div>

        {/* Confirm new audit dialog */}
        {confirmNew && (
          <div className="confirm-box">
            <p>You have unsaved changes. Save before starting a new audit?</p>
            <div className="confirm-actions">
              <button className="btn btn-primary btn-sm" onClick={() => { handleSave(); confirmNewAudit(); }}>
                Save Then Start New
              </button>
              <button className="btn btn-outline btn-sm" onClick={confirmNewAudit}>
                Discard & Start New
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmNew(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Saved audits list */}
        <div className="modal-section">
          <div className="modal-section-title">
            Saved Audits ({savedAudits.length})
          </div>

          {savedAudits.length === 0 ? (
            <div className="modal-empty">No saved audits yet. Save the current audit to create a record.</div>
          ) : (
            <div className="saved-audit-list">
              {savedAudits.map((audit) => (
                <div key={audit.id} className={`saved-audit-item ${audit.id === currentAuditId ? "current" : ""}`}>
                  <div className="saved-audit-body">
                    <div className="saved-audit-company">
                      {audit.companyName || "Unnamed"}
                      {audit.id === currentAuditId && (
                        <span className="current-tag">Current</span>
                      )}
                    </div>
                    {audit.facilityName && (
                      <div className="saved-audit-meta">{audit.facilityName}</div>
                    )}
                    <div className="saved-audit-meta">
                      Audit Date: {audit.auditDate || "—"} &nbsp;·&nbsp;
                      Auditor: {audit.auditorName || "—"} &nbsp;·&nbsp;
                      {audit.categoryCount} program{audit.categoryCount !== 1 ? "s" : ""}
                    </div>
                    <div className="saved-audit-saved">
                      Saved: {new Date(audit.savedAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="saved-audit-actions">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleLoad(audit)}
                      disabled={audit.id === currentAuditId}
                    >
                      Open
                    </button>
                    {confirmDelete === audit.id ? (
                      <>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(audit.id)}
                        >
                          Confirm Delete
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setConfirmDelete(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setConfirmDelete(audit.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <p className="modal-note">
            Audits are saved locally in your browser. For long-term storage or client sharing, use the CSV or JSON export on the Dashboard or Findings page.
          </p>
        </div>
      </div>
    </div>
  );
}
